// SF3 — branded versus generic, measured on clicks.
import React from 'react';
import s from '../TermPerformance.module.css';
import { num, pct } from '../format';

const ORDER = [['generic', 'Generic'], ['competitor', "A competitor's brand"],
               ['branded', 'Your brand']];

export default function BrandedSplit({ data, onFilter }) {
  if (!data) return <div className={s.empty}>Loading…</div>;
  if (!data.current?.total_clicks) {
    return <div className={s.empty}>No clicks in this window to split.</div>;
  }
  const now = data.current.share_of_clicks;
  const was = data.previous?.share_of_clicks;

  return (
    <>
      <div className={s.splitBar}>
        {ORDER.filter(([k]) => now[k] > 0).map(([k, label]) => (
          <span key={k} className={`${s.splitSeg} ${s[`mix_${k}`]}`}
                style={{ width: `${now[k] * 100}%` }} title={`${label}: ${pct(now[k], 0)}`}>
            {now[k] > 0.08 ? pct(now[k], 0) : ''}
          </span>
        ))}
      </div>

      <div className={s.stats}>
        {ORDER.map(([k, label]) => (
          <div key={k} className={s.stat}>
            <span className={s.statValue}>{pct(now[k], 0)}</span>
            <span className={s.statLabel}>
              {label}
              {was && was[k] != null && (
                <span className={now[k] >= was[k] ? s.up : s.down}>
                  {' '}{now[k] >= was[k] ? '▲' : '▼'}
                  {Math.abs((now[k] - was[k]) * 100).toFixed(1)}pp
                </span>
              )}
            </span>
          </div>
        ))}
        <div className={s.stat}>
          <span className={s.statValue}>{num(data.current.total_clicks)}</span>
          <span className={s.statLabel}>clicks measured</span>
        </div>
      </div>

      <p className={s.hint} style={{ margin: '4px 0 14px' }}>
        Measured on clicks, not on how many terms fall each way. A seller may hold two
        hundred generic terms and eight branded ones with the eight carrying most of the
        traffic — counting terms would report the opposite of the truth.
        {' '}A competitor&apos;s brand term is branded demand, but not yours, so it is
        counted as its own class rather than folded into either side.
        {data.compared_to && <> Compared with {data.compared_to}.</>}
      </p>

      <div className={s.chipRow}>
        {ORDER.map(([k, label]) => (
          <button key={k} className={s.chip} onClick={() => onFilter(k)}>
            Show {label.toLowerCase()} terms
          </button>
        ))}
      </div>

      <OwnBrand data={data.own_brand} />
    </>
  );
}

// How much of their own name the seller actually holds.
//
// The split above says how much traffic is branded. This says whether that
// traffic is theirs: on the terms carrying their brand, what share of the
// impressions do they get? Measured on impressions rather than clicks, because
// the question is who is being shown on the seller's name — a rival that
// appears and is passed over has still taken the slot.
function OwnBrand({ data }) {
  if (!data) return null;
  if (data.impression_share == null) {
    return <p className={s.hint} style={{ marginTop: 18 }}>{data.note}</p>;
  }
  const impressions = data.impression_share;
  const clicks = data.click_share;
  return (
    <section style={{ marginTop: 22 }}>
      <h4 className={s.expansionTitle}>Your own brand terms</h4>
      <p className={s.hint} style={{ margin: '4px 0 12px' }}>
        On the {num(data.terms)} terms carrying {data.brands.join(', ')}, you hold{' '}
        <strong>{pct(impressions, 1)}</strong> of the impressions and{' '}
        <strong>{pct(clicks, 1)}</strong> of the clicks.
        {clicks != null && impressions != null && clicks > impressions * 1.2 && (
          <> You win more of the clicks than the shelf you are given, so when shoppers
            searching your name do see you they choose you — the shortfall is visibility,
            not appeal.</>
        )}
      </p>
      {data.weakest?.length > 0 && (
        <table className={s.subTable}>
          <thead>
            <tr>
              <th>Term carrying your brand</th>
              <th className={s.right}>Your impression share</th>
              <th className={s.right}>Impressions on the term</th>
            </tr>
          </thead>
          <tbody>
            {data.weakest.map((t) => (
              <tr key={t.search_term}>
                <td>{t.search_term}</td>
                <td className={s.right}>{pct(t.impression_share, 1)}</td>
                <td className={s.right}>{num(t.market_impressions)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <p className={s.hint} style={{ marginTop: 10 }}>
        Ranked by how little of each term you hold rather than by its size — a term with
        your brand in it that you barely appear on is the finding whatever its volume.
      </p>
    </section>
  );
}
